import { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProgramRoleMenuPermissions } from '@/modules/programs/hooks/useProgramRoleMenuPermissions';
import { resolveDashboardLandingPath } from '@/modules/programs/dashboard-governance/DashboardLandingResolver';

/**
 * UNIFIED WORKSPACE ARCHITECTURE:
 * All roles now access a single workspace at /programs/:programId/workspace
 * The workspace content adapts based on the user's role and permissions.
 * 
 * RouteKind is simplified to:
 * - 'dashboard' → SuperAdmin only, others redirect to workspace
 * - 'workspace' → All operational roles (content adapts internally)
 */
type RouteKind = 'dashboard' | 'workspace';

interface RoleWorkspaceRouteGuardProps {
  children: ReactNode;
  /** @deprecated routeKind is simplified - use 'dashboard' or 'workspace' only */
  routeKind: RouteKind | 'program' | 'hospital' | 'hcp' | 'patient' | 'pharma';
}

/**
 * RoleWorkspaceRouteGuard
 * 
 * UNIFIED WORKSPACE ARCHITECTURE:
 * - ALL non-SuperAdmin roles access via single /workspace route
 * - Workspace content adapts based on role/permissions
 * - Dashboard is for SuperAdmin only
 */
export default function RoleWorkspaceRouteGuard({ children, routeKind }: RoleWorkspaceRouteGuardProps) {
  const { user } = useAuth();
  const { programId } = useParams<{ programId: string }>();
  const { dashboardKey, landingRoute, defaultLandingRoute, isLoading } = useProgramRoleMenuPermissions({
    programId,
    enabled: !!programId && user?.role !== 'SuperAdmin' && user?.role !== 'ProductAdmin',
  });

  if (!programId) {
    return <>{children}</>;
  }

  // Wait for menu permissions to load before making redirect decisions.
  // Without this, default values would cause premature redirects that
  // override the user's configured role-based landing route.
  if (isLoading) {
    return <>{children}</>;
  }

  const resolvedLandingRoute = resolveDashboardLandingPath({
    programId,
    dashboardKey: dashboardKey || user?.dashboardKey,
    landingRoute: landingRoute || user?.landingRoute,
    defaultLandingRoute,
  });

  // ─────────────────────────────────────────────────────────────
  // DASHBOARD ROUTE: Only for SuperAdmin, redirect others
  // ─────────────────────────────────────────────────────────────
  if (routeKind === 'dashboard') {
    // SuperAdmin can access dashboard
    if (user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin') {
      return <>{children}</>;
    }
    
    return <Navigate to={resolvedLandingRoute} replace />;
  }

  // ─────────────────────────────────────────────────────────────
  // WORKSPACE ROUTES: Single unified workspace for all operational roles
  // Legacy routeKinds (hospital, hcp, patient, pharma, program) all map to workspace
  // ─────────────────────────────────────────────────────────────
  
  // All non-SuperAdmin program users can access the unified workspace
  return <>{children}</>;
}